import { useQuery } from "@tanstack/react-query";

import useAppStore from "@/store/appStateZustand";

export type SignupEligibilityReason = "geo" | "vpn";
export type SignupEligibilitySignupPath = "tunecore";

export type SignupEligibility = {
  allowed: boolean;
  reason?: SignupEligibilityReason;
  signupPath?: SignupEligibilitySignupPath;
};

const ELIGIBILITY_TIMEOUT_MS = 5000;

// Fails open — the backend signup mutation is the authoritative gate.
// Timeout prevents a stalled sa-api from keeping the sign-up spinner forever.
export async function fetchSignupEligibility(apiUrl: string | undefined): Promise<SignupEligibility> {
  if (apiUrl === undefined || apiUrl === "") {
    return { allowed: true };
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ELIGIBILITY_TIMEOUT_MS);
  try {
    const response = await fetch(`${apiUrl}/signup-eligibility`, { signal: controller.signal });
    if (!response.ok) {
      return { allowed: true };
    }
    const body = await response.json() as Partial<SignupEligibility>;
    return {
      allowed: body.allowed !== false,
      ...(body.reason ? { reason: body.reason } : {}),
      ...(body.signupPath ? { signupPath: body.signupPath } : {}),
    };
  }
  catch {
    return { allowed: true };
  }
  finally {
    clearTimeout(timer);
  }
}

export function useSignupEligibility() {
  const apiUrl = useAppStore(state => state.apiUrl);
  return useQuery({
    queryKey: ["signup-eligibility", apiUrl],
    queryFn: async () => fetchSignupEligibility(apiUrl),
    staleTime: Infinity,
  });
}
